/**
 * BillType: Defines different billing methods for calculating messaging volume.
 *
 * Used in:
 * - Org.defaultBillType: Specifies the preferred billing method for an org
 * - Billing.volumeSource: Records how volume was actually fetched for a bill
 *
 * Values:
 * - standard: Excludes 'failed' and 'waiting' status (current method)
 * - conservative: Excludes 'failed', 'waiting', and 'sent' status (only delivered/read)
 * - karix: Fetches volume from uploaded Karix CSV data
 */
export enum BillType {
  STANDARD = 'standard',
  CONSERVATIVE = 'conservative',
  KARIX = 'karix',
}

/**
 * Statuses considered billable under each BillType.
 *
 * - Standard: excludes 'failed' and 'waiting' (charges from 'sent' onwards).
 * - Conservative / Karix: excludes 'failed', 'waiting', and 'sent' (charges from 'delivered' onwards).
 */
export const BILLABLE_STATUSES_STANDARD: ReadonlySet<string> = new Set([
  'sent',
  'delivered',
  'read',
  'responded',
  'clicked',
  'clicked_responded',
]);

export const BILLABLE_STATUSES_CONSERVATIVE: ReadonlySet<string> = new Set([
  'delivered',
  'read',
  'responded',
  'clicked',
  'clicked_responded',
]);

export const getBillableStatuses = (billType: BillType): ReadonlySet<string> =>
  billType === BillType.STANDARD
    ? BILLABLE_STATUSES_STANDARD
    : BILLABLE_STATUSES_CONSERVATIVE;

/**
 * Statuses considered billable for RCS (v1): OUTBOUND, everything the provider
 * accepted — from submission ('waiting' / 'sent') through delivery/read. Bills on
 * submission (Route Mobile charges per submitted message), so a message sitting at
 * 'waiting' with no delivery callback still counts. Excludes ONLY 'failed' (incl.
 * WhatsApp-fallback, which RCS maps to failed) and 'received' (inbound). Kept as
 * its own set — independent of BillType, which doesn't apply to RCS — so adjusting
 * the policy later (e.g. billing inbound by adding 'received', or dropping
 * 'waiting') is a single-line change here.
 *
 * Safe with the FINAL (ReplacingMergeTree) scans: each message is counted once at
 * its latest status, so waiting→delivered is not double-counted.
 */
export const BILLABLE_STATUSES_RCS: ReadonlySet<string> = new Set([
  'waiting',
  'sent',
  'delivered',
  'read',
  'responded',
  'clicked',
  'clicked_responded',
]);
